import { DAY_MARKS, type DayMarkKind } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { useOnResume } from '../lib/fresh.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';

/**
 * The day's marks, as chips you can see. The fixes pass, 13 October 2026
 * (docs/pitch-2026-10-12/fixes/mocks/marks.html), replacing the button that
 * opened a list of eight and showed nothing at rest.
 *
 * Solid: a mark he set. Dashed: a mark the door set from what he said — tap
 * it to keep it (it turns solid), or take it off with the ×. "+" shows the
 * rest; tapping one sets it. No count out of a total, no label on a mark,
 * nothing scored.
 *
 * The face-up rule survives as a switch (Settings → App, "Hide marks at
 * rest"): on, the row reads "Marks · 2" until tapped, so a phone on a table
 * says nothing. It is off by default, because a control you cannot read is
 * the thing he called clunky.
 */
export function MarkChips({ day }: { day: string }): JSX.Element {
  const { user } = useSession();
  const [marks, setMarks] = useState<{ kind: string; source: 'you' | 'app' }[] | null>(null);
  const [more, setMore] = useState(false);
  const [shown, setShown] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const load = (): void => {
    void api
      .dayMarks(day, day)
      .then((rows) => setMarks(rows.map((r) => ({ kind: r.kind, source: r.source ?? 'you' }))))
      .catch(() => setMarks([]));
  };
  useEffect(() => { setMarks(null); setMore(false); setShown(false); load(); }, [day]);
  // A mark the app set while the app was away (a short night from Health's
  // sleep) arrives on coming back, in place (20 Oct 2026).
  useOnResume(load);

  const on = new Map((marks ?? []).map((m) => [m.kind, m.source]));

  async function toggle(kind: DayMarkKind): Promise<void> {
    setFailed(null);
    try {
      await api.setDayMark(day, kind);
      load();
    } catch {
      setFailed('That did not save.');
    }
  }
  async function keep(kind: DayMarkKind): Promise<void> {
    setFailed(null);
    try {
      await api.keepDayMark(day, kind);
      load();
    } catch {
      setFailed('That did not save.');
    }
  }

  const hidden = (user?.hideMarksAtRest ?? false) && !shown;
  const set = DAY_MARKS.filter((m) => on.has(m.kind));
  const rest = DAY_MARKS.filter((m) => !on.has(m.kind));

  if (marks === null) return <div className="marks" aria-busy="true" />;

  if (hidden) {
    return (
      <div className="marks">
        <button type="button" className="chip chip-quiet" onClick={() => setShown(true)}>
          {set.length === 0 ? 'Mark the day' : `Marks · ${set.length}`}
        </button>
      </div>
    );
  }

  return (
    <div className="marks" role="group" aria-label="Marks on this day">
      {set.map((m) => {
        const app = on.get(m.kind) === 'app';
        return (
          <span key={m.kind} className={`chip chip-mark${app ? ' chip-app' : ''}`}>
            {app ? (
              <button type="button" className="chip-mark-main" onClick={() => void keep(m.kind)} title="The door set this from what you said. Tap to keep it.">
                {m.label}
              </button>
            ) : (
              <span className="chip-mark-main">{m.label}</span>
            )}
            <button type="button" className="chip-x" aria-label={`Take ${m.label} off`} onClick={() => void toggle(m.kind)}>
              ×
            </button>
          </span>
        );
      })}
      {!more ? (
        <button type="button" className="chip chip-add" aria-label="Mark something else" onClick={() => setMore(true)}>
          {set.length === 0 ? 'Mark the day' : '+'}
        </button>
      ) : (
        rest.map((m) => (
          <button key={m.kind} type="button" className="chip" onClick={() => void toggle(m.kind)}>
            {m.label}
          </button>
        ))
      )}
      {more && rest.length === 0 && <span className="faint">All eight are on.</span>}
      {set.some((m) => on.get(m.kind) === 'app') && <span className="faint marks-hint">Dashed: the door’s pick. Tap to keep it, × to take it off.</span>}
      {failed && <span className="faint" role="alert">{failed}</span>}
    </div>
  );
}
